import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EntityPanelSelector, { LAYOUT_MODES } from '@common/components/EntityPanelSelector';
import { loadBusinessServices } from '@api/filters';
import * as styles from './BusinessServiceFilter.module.css';

/**
 * BusinessServiceFilter Component
 * Uses EntityPanelSelector in TWO_COLUMNS mode (no filter dropdown)
 */
function BusinessServiceFilter({
  selectedBusinessServiceIds,
  onSelectionChange,
}) {
  const { t } = useTranslation();
  const [availableBusinessServices, setAvailableBusinessServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load business services
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const businessServices = await loadBusinessServices();
        setAvailableBusinessServices(businessServices);
      } catch (error) {
        console.error('Error loading business services:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Labels for EntityPanelSelector
  const labels = useMemo(() => ({
    leftPanelTitle: t('reports.filters.availableEntities'),
    rightPanelTitle: t('reports.filters.selectedEntities'),
    selectAll: t('common.selectAll'),
    clearAll: t('common.clearAll'),
    noItemsAvailable: t('reports.filters.noBusinessServicesAvailable'),
    noItemsFound: t('reports.filters.noBusinessServicesFound'),
    noItemsSelected: t('reports.filters.noEntitiesSelected'),
  }), [t]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <EntityPanelSelector
      mode={LAYOUT_MODES.TWO_COLUMNS}
      availableItems={availableBusinessServices}
      selectedItemIds={selectedBusinessServiceIds}
      onSelectionChange={onSelectionChange}
      labels={labels}
      valueField="dbId"
    />
  );
}

BusinessServiceFilter.propTypes = {
  selectedBusinessServiceIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export { BusinessServiceFilter };
export default BusinessServiceFilter;
